/**
 * React Query hooks for the job launcher, the job history and the popular-times page.
 * Start calls return at once (202 + jobId); runs are polled until they finish.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { adminJobsApi } from '@api/endpoints/adminJobs'
import { popularityApi } from '@api/endpoints/popularity'
import { statisticsApi } from '@api/endpoints/statistics'
import type { PopularityOptionsResponse, UpdatePopularityOptionsRequest } from '@api/types/popularity'
import { isFinished } from '../popularTimes'

export const ADMIN_JOBS_KEY = ['admin-jobs']
export const JOB_HISTORY_KEY = ['statistics-job-history']
export const POPULARITY_OPTIONS_KEY = ['popularity-options']
export const POPULARITY_STATUS_KEY = ['popularity-status']
export const POPULARITY_SYNTHETIC_KEY = ['popularity-synthetic']

/** Axios error → the server's `{ message }` (or `Message`), else a fallback. */
export function apiErrorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data
  if (data && typeof data === 'object') {
    const d = data as { message?: string; Message?: string; title?: string }
    if (d.message || d.Message) return (d.message ?? d.Message) as string
    if (d.title) return d.title
  }
  if (typeof data === 'string' && data.length > 0 && data.length < 500) return data
  return error instanceof Error ? error.message : fallback
}

/** The launchable jobs; polled every 4 s while any of them runs. */
export function useAdminJobs(enabled = true) {
  return useQuery({
    queryKey: ADMIN_JOBS_KEY,
    queryFn: () => adminJobsApi.list(),
    enabled,
    refetchInterval: (q) => (q.state.data?.some((j) => j.isRunning) ? 4000 : false),
    refetchOnWindowFocus: true,
  })
}

/** One page of the job history; polled while a row on it is still running. */
export function useJobHistory(page: number, pageSize: number) {
  return useQuery({
    queryKey: [...JOB_HISTORY_KEY, page, pageSize],
    queryFn: () => statisticsApi.getLogs({ page: page + 1, pageSize }),
    refetchInterval: (q) => (q.state.data?.logs?.some((l) => !isFinished(l.status)) ? 4000 : false),
  })
}

/** One run, polled every 2 s until it is finished. */
export function useJobRun(jobId: string | null) {
  return useQuery({
    queryKey: ['admin-job-run', jobId],
    queryFn: () => adminJobsApi.getRun(jobId as string),
    enabled: !!jobId,
    refetchInterval: (q) => (q.state.data && isFinished(q.state.data.status) ? false : 2000),
  })
}

/** After any launch or finish: refresh the job list, the history and the popular-times panels. */
export function useRefreshJobViews() {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: ADMIN_JOBS_KEY })
    qc.invalidateQueries({ queryKey: JOB_HISTORY_KEY })
    qc.invalidateQueries({ queryKey: POPULARITY_STATUS_KEY })
    qc.invalidateQueries({ queryKey: POPULARITY_SYNTHETIC_KEY })
  }
}

export function useStartJob() {
  const refresh = useRefreshJobViews()
  return useMutation({
    mutationFn: ({ key, parameters }: { key: string; parameters?: object }) => adminJobsApi.start(key, parameters),
    onSettled: refresh,
  })
}

// ---------------------------------------------------------------- popular times

export function usePopularityOptions() {
  return useQuery({ queryKey: POPULARITY_OPTIONS_KEY, queryFn: () => popularityApi.getOptions(), staleTime: 30_000, refetchOnWindowFocus: false })
}

export function useUpdatePopularityOptions() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (request: UpdatePopularityOptionsRequest) => popularityApi.updateOptions(request),
    onSuccess: (data: PopularityOptionsResponse) => qc.setQueryData(POPULARITY_OPTIONS_KEY, data),
  })
}

export function useResetPopularityOptions() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => popularityApi.resetOptions(),
    onSuccess: (data: PopularityOptionsResponse) => qc.setQueryData(POPULARITY_OPTIONS_KEY, data),
  })
}

export function usePopularityStatus() {
  return useQuery({ queryKey: POPULARITY_STATUS_KEY, queryFn: () => popularityApi.getStatus(), refetchOnWindowFocus: true })
}

export function useSyntheticItems() {
  return useQuery({ queryKey: POPULARITY_SYNTHETIC_KEY, queryFn: () => popularityApi.listSynthetic() })
}
